import * as UI from "@iyk/music-ui"
import { AppLink } from "@iyk/react-router/client"
import { AppResponse, HttpRequest } from "@iyk/react-router/server"
import { Effect } from "effect"
import React from "react"
import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router"
import type { Route } from "./+types/root"
import stylesheet from "./app.css?url"
import { AppRoutes } from "./AppRoutes.ts"
import { AppHandler } from "./server/AppHandler.ts"

export const links: Route.LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
  { rel: "apple-touch-icon", sizes: "180x180", href: UI.Assets.AppleTouchIconPngUrl },
  { rel: "icon", type: "image/svg+xml", href: UI.Assets.FaviconSvgUrl }
]

export const loader = AppHandler.loader("root")(HttpRequest.get.build()).handle((_, context) => {
  return Effect.gen(function* () {
    const toasts = yield* context.toasts.flash
    return AppResponse.Json({ toasts })
  })
})

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="dark bg-gray-1 text-gray-12">
        <UI.BaseUIWrapper>{children}</UI.BaseUIWrapper>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function App({ loaderData }: Route.ComponentProps) {
  const toast = UI.useToast()

  React.useEffect(() => {
    for (const message of loaderData.toasts) {
      toast.add({
        title: message.title,
        description: message.description,
        type: message.type
      })
    }
  }, [loaderData.toasts])

  return <Outlet />
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!"
  let details = "An unexpected error occurred."
  let stack: string | undefined

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error"
    details = error.status === 404 ? "The requested page could not be found." : error.statusText || details
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message
    stack = error.stack
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {/* TODO: Should this be an world index page which can work out where to send the user? */}
      <p>
        Return to <AppLink to={AppRoutes.home}>home</AppLink>
      </p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  )
}
